import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArcheHandoffIntake, type ArcheHandoffPayload } from '@/components/fias-ai';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

/**
 * The Chat arche's handoff intake (Arche Handoff, UI plane — the `arche_chat`
 * entry of `ARCHE_HANDOFF_INTAKE`). Consumes `'open_channel'`: another arche —
 * the LitCraft hub's Chat button, via the bridge's `open_arche` — hands the
 * user a channel by reference, and Chat opens it.
 *
 * A channel id is a POINTER, never access. Before opening it the channel is
 * re-read under the user's own membership (`GET /channels/:id` refuses a
 * non-member); a channel the user cannot see leaves them on the Chat home with
 * a notice instead of an empty, erroring channel pane. The producer is
 * expected to make the user a member first (the hub calls `join_guild_chat`),
 * so the notice is the honest fallback, not the normal path.
 *
 * Mounted on the arche wrapper, not on ChatPage: ChatPage is a different route
 * element for "/" and "/channel/:id", and the intake must survive that switch.
 */
export function useChatHandoffIntake(): void {
  const navigate = useNavigate();

  const applyChatHandoff = useCallback(
    (payload: ArcheHandoffPayload) => {
      if (payload.kind !== 'open_channel') return;
      const { channelId } = payload;
      void (async () => {
        try {
          await api.getChatChannel(channelId);
        } catch {
          toast({
            title: "You're not in that chat",
            description: 'Join it first, then open it again.',
          });
          return;
        }
        navigate(`/a/arche_chat/channel/${channelId}`, { replace: true });
      })();
    },
    [navigate],
  );

  useArcheHandoffIntake('arche_chat', applyChatHandoff);
}
